import * as uiAnalyticsConsumer from '@servicenow/ui-analytics-consumer';
import {applyInteractionId, getInteractionId} from '@servicenow/ui-metrics';
import {beforeNextTick, emitNowUiEvent, getSpanOptions} from '../utils';
import {createContext} from './createContext';
import {
	COMPONENT_ID,
	CONFIG,
	DISPATCH,
	RETARGETED_ACTION_TYPES,
	BOOTSTRAPPED_HANDLED,
	CONNECTED_HANDLED
} from '../symbols';
import {sandboxEffect} from './sandboxEffect';
import {
	ACTION_HANDLED,
	COMPONENT_BOOTSTRAPPED,
	COMPONENT_CONNECTED,
	COMPONENT_DISCONNECTED,
	SEISMIC_HOIST_TAG_NAME,
	COMPONENT_PROPERTY_UPDATE_REQUESTED,
	COMPONENT_RENDER_REQUESTED,
	COMPONENT_TREE_RENDERED,
	COMPONENT_RENDERED,
	COMPONENT_DOM_READY,
	COMPONENT_DOM_TREE_READY,
	SPAN_KIND_CLIENT,
	SPAN_ATTR_DISPATCH_ACTION_TYPE,
	SPAN_ATTR_COMPONENT_EVENT_DEFAULT_PREVENTED,
	SPAN_ATTR_COMPONENT_EVENT_PROPAGATION_STOPPED,
	SPAN_LIFECYCLE_EVENTS,
	SPAN_FOR_ACTION_HANDLED
} from '../constants';
import {normalizeActionHandler} from '../utils/config/normalizeActionHandler';
import getHostVariables from '../utils/getHostVariables';
import {addActionHandler} from './customEventActionHandler';
import {onActionHoisted} from './onActionHoisted';
import {wasActionDispatchedByDescendant} from './utils';
const {trace, isValidTraceId} = uiAnalyticsConsumer;
// defer the execution of the origin handler to support preventDefault
function runEffects(fn, isOrigin) {
	return isOrigin ? beforeNextTick(() => fn()) : fn();
}

export function onAction(targetEl, targetSelector, host, actionType, action = {}) {
	const hostVariables = getHostVariables(host);
	const {type = actionType, meta = {path: [], eventHandler: false}} = action;
	const {path, eventHandler, event, capture} = meta;
	const [origin] = path;
	const isOrigin = hostVariables[COMPONENT_ID] === origin;

	if (eventHandler)
		// interaction ids aren't applied to native events dispacthed by the browser
		applyInteractionId(meta);

	const isImmediate = [
		COMPONENT_DISCONNECTED,
		COMPONENT_DOM_READY,
		COMPONENT_DOM_TREE_READY,
		COMPONENT_TREE_RENDERED,
		COMPONENT_RENDERED,
		COMPONENT_RENDER_REQUESTED,
		COMPONENT_PROPERTY_UPDATE_REQUESTED
	].includes(actionType);

	if (event && !eventHandler) {
		const hoistElm = event.composedPath().find(elem => elem.tagName === SEISMIC_HOIST_TAG_NAME);
		const hoistElmVariables = getHostVariables(hoistElm);
		if (
			hoistElm &&
			hoistElmVariables &&
			!hoistElmVariables[RETARGETED_ACTION_TYPES].some(actionType => actionType === type)
		) {
			addActionHandler(
				hoistElm,
				targetSelector,
				hoistElm.sourceElement,
				type,
				capture,
				eventHandler,
				onActionHoisted
			);
			hoistElmVariables[RETARGETED_ACTION_TYPES] = [
				...hoistElmVariables[RETARGETED_ACTION_TYPES],
				type
			];
		}
	}

	runEffects(() => {
		const startTime = performance.now();
		const interceptors =
			hostVariables[CONFIG].interceptors[type] &&
			hostVariables[CONFIG].interceptors[type].has(targetSelector)
				? hostVariables[CONFIG].interceptors[type].get(targetSelector)
				: null;

		if (!interceptors || !interceptors.length) {
			return;
		}

		const onActionSpan = trace(SPAN_FOR_ACTION_HANDLED, {
			...getSpanOptions(host),
			traceId: getInteractionId(action.meta),
			kind: SPAN_KIND_CLIENT,
			parentSpan: action.meta.span
		});
		onActionSpan.setAttribute(SPAN_ATTR_DISPATCH_ACTION_TYPE, SPAN_LIFECYCLE_EVENTS[type] || type);

		const {length: interceptorCount} = interceptors;
		if ((isOrigin && action.defaultPrevented) || (!isOrigin && action.propagationStopped)) {
			if (isOrigin) {
				onActionSpan.setAttribute(SPAN_ATTR_COMPONENT_EVENT_DEFAULT_PREVENTED, true);
			} else {
				onActionSpan.setAttribute(SPAN_ATTR_COMPONENT_EVENT_PROPAGATION_STOPPED, true);
			}
			onActionSpan.end();
			return;
		}

		if (actionType === COMPONENT_BOOTSTRAPPED) hostVariables[BOOTSTRAPPED_HANDLED] = true; // eslint-disable-line no-param-reassign
		if (actionType === COMPONENT_CONNECTED) hostVariables[CONNECTED_HANDLED] = true; // eslint-disable-line no-param-reassign
		if (isValidTraceId(onActionSpan.traceId)) action.meta.actionSpan = onActionSpan;

		const context = createContext(host, action);
		let ctx = context;
		for (let i = 0; i < interceptorCount; i++) {
			const interceptor = interceptors[i];
			ctx = interceptor.fn(ctx) || ctx;
		}

		const {effects} = ctx;
		const {length: effectCount} = effects;
		for (let i = 0; i < effectCount; i++) {
			const effect = effects[i];
			if (typeof effect === 'function') {
				const normalizedEffect = normalizeActionHandler(effect, host.tagName.toLowerCase());
				sandboxEffect(
					normalizedEffect,
					hostVariables[DISPATCH],
					action,
					host
				)(...normalizedEffect.args);
				continue;
			}

			if (!effect.descendantOnly || wasActionDispatchedByDescendant(action.composedPath(), host))
				sandboxEffect(effect, hostVariables[DISPATCH], action, host)(...effect.args);
		}
		onActionSpan.end();
		const endTime = performance.now();
		emitNowUiEvent(ACTION_HANDLED, {
			host,
			action,
			targetSelector,
			interceptors,
			context: ctx,
			duration: endTime - startTime
		});
	}, isOrigin && !eventHandler && !isImmediate);
}
